import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, SlidersHorizontal, X } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, type CommunitySummary } from "@/lib/api";
import { useLiveProperties } from "@/hooks/useLiveProperties";

const propertyTypes = [
  { value: "any", label: "Any Type" },
  { value: "apartment", label: "Apartment" },
  { value: "villa", label: "Villa" },
  { value: "townhouse", label: "Townhouse" },
  { value: "penthouse", label: "Penthouse" },
  // "Studio" is technically a bedroom count, not a property type, but is
  // surfaced here as a top-level type per the May 2026 content brief.
  // Submit handler translates it to a 0-bedroom apartment filter.
  { value: "studio", label: "Studio" },
];

const beds = [
  { value: "any", label: "Any" },
  { value: "0", label: "Studio" },
  { value: "1", label: "1 Bedroom" },
  { value: "2", label: "2 Bedrooms" },
  { value: "3", label: "3 Bedrooms" },
  { value: "4", label: "4+ Bedrooms" },
];

const baths = [
  { value: "any", label: "Any" },
  { value: "1", label: "1+" },
  { value: "2", label: "2+" },
  { value: "3", label: "3+" },
  { value: "4", label: "4+" },
  { value: "5", label: "5+" },
];

const priceRanges = [
  { value: "any", label: "Any Budget" },
  { value: "0-500000", label: "Under AED 500K" },
  { value: "500000-1000000", label: "AED 500K – 1M" },
  { value: "1000000-2000000", label: "AED 1M – 2M" },
  { value: "2000000-5000000", label: "AED 2M – 5M" },
  { value: "5000000-10000000", label: "AED 5M – 10M" },
  { value: "10000000-", label: "AED 10M+" },
];

type ListingTab = "sale" | "rent";
type SearchTab = ListingTab | "sell-let";

interface SearchBarProps {
  defaultListingType?: ListingTab;
  className?: string;
  variant?: "hero" | "inline";
}

export function SearchBar({
  defaultListingType = "sale",
  className,
  variant = "inline",
}: SearchBarProps) {
  const navigate = useNavigate();
  const [listingType, setListingType] = useState<SearchTab>(defaultListingType);
  const [community, setCommunity] = useState("any");
  const [propertyType, setPropertyType] = useState("any");
  const [beds_, setBeds] = useState("any");
  const [price, setPrice] = useState("any");
  const [communities, setCommunities] = useState<CommunitySummary[]>([]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [baths_, setBaths] = useState("any");
  const [minArea, setMinArea] = useState("");
  const [maxArea, setMaxArea] = useState("");
  const [newOnly, setNewOnly] = useState(false);
  const { version } = useLiveProperties();

  useEffect(() => {
    setListingType(defaultListingType);
  }, [defaultListingType]);

  useEffect(() => {
    let cancelled = false;
    api
      .listCommunities()
      .then(({ communities }) => !cancelled && setCommunities(communities))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [version]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (listingType === "sell-let") {
      navigate("/list-property");
      return;
    }
    const params = new URLSearchParams();
    params.set("listingType", listingType);
    if (community !== "any") params.set("community", community);
    if (propertyType === "studio") {
      // Studio = apartment with 0 bedrooms — the editorial brief lists
      // Studio under TYPE but the data model treats it as a bedroom count.
      params.set("propertyType", "apartment");
      params.set("maxBedrooms", "0");
      params.set("minBedrooms", "0");
    } else {
      if (propertyType !== "any") params.set("propertyType", propertyType);
      if (beds_ === "4") {
        // "4+ Bedrooms" is the only open-ended option.
        params.set("minBedrooms", "4");
      } else if (beds_ !== "any") {
        // Exact bedroom count (Studio = 0, 1, 2, 3) — pin both bounds so
        // choosing "2 Bedrooms" returns 2-bed homes, not "2+".
        params.set("minBedrooms", beds_);
        params.set("maxBedrooms", beds_);
      }
    }
    if (price !== "any") {
      const [min, max] = price.split("-");
      if (min) params.set("minPrice", min);
      if (max) params.set("maxPrice", max);
    }
    if (baths_ !== "any") params.set("minBathrooms", baths_);
    if (minArea && Number(minArea) > 0) params.set("minArea", String(Number(minArea)));
    if (maxArea && Number(maxArea) > 0) params.set("maxArea", String(Number(maxArea)));
    if (newOnly) params.set("newDays", "7");
    navigate(`/properties?${params.toString()}`);
  };

  const advancedActiveCount =
    (baths_ !== "any" ? 1 : 0) +
    (minArea ? 1 : 0) +
    (maxArea ? 1 : 0) +
    (newOnly ? 1 : 0);

  const isHero = variant === "hero";

  return (
    <form
      onSubmit={submit}
      className={cn(
        "w-full",
        isHero ? "border border-[var(--line-strong)] bg-[var(--surface)] shadow-sm" : "",
        className,
      )}
    >
      {/* Tabs share the row equally but never wrap mid-label; if a very
          narrow screen can't fit the labels, the row scrolls sideways. The
          scroller overlaps the wrapper's rule by 1px so the active
          underline sits on it without the scroller overflowing vertically. */}
      <div className="border-b border-[var(--line)]">
        <div className="-mb-px flex overflow-x-auto px-1 [scrollbar-width:none] md:px-4 [&::-webkit-scrollbar]:hidden">
          {(["sale", "rent", "sell-let"] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setListingType(t)}
              className={cn(
                "relative min-h-[44px] flex-1 whitespace-nowrap px-2 py-3.5 text-[10.5px] uppercase tracking-[0.16em] transition-colors sm:flex-initial sm:px-4 sm:text-[11px] sm:tracking-[0.2em] md:px-5 md:tracking-[0.22em]",
                listingType === t
                  ? "text-[var(--text)]"
                  : "text-[var(--text-muted)] hover:text-[var(--text-soft)]",
              )}
            >
              {t === "sale"
                ? "Buy"
                : t === "rent"
                  ? "Rent"
                  : "Sell / Let"}
              {listingType === t && (
                <span aria-hidden className="absolute inset-x-2 bottom-0 h-px bg-[var(--cream)] sm:inset-x-3" />
              )}
            </button>
          ))}
        </div>
      </div>

      {listingType === "sell-let" ? (
        <div className="flex flex-col gap-4 px-4 py-6 sm:flex-row sm:items-center sm:justify-between md:px-6 md:py-7">
          <p className="max-w-[48ch] text-[14px] leading-[1.6] text-[var(--text-soft)]">
            Thinking of selling or letting your property? Get a free, no-obligation
            valuation from advisors who know your community.
          </p>
          <button
            type="submit"
            className="inline-flex min-h-[52px] flex-none items-center justify-center gap-3 bg-[var(--cream)] px-8 text-[11.5px] uppercase tracking-[0.22em] text-[var(--navy)] transition-colors hover:bg-[#e6dab0]"
          >
            Request a Valuation
            <ArrowRight className="size-4" />
          </button>
        </div>
      ) : (
      <>
      {/* Phones stack the fields; tablets pair them 2×2 with the button
          full-width beneath (a single row overflows at 768px); the
          five-column strip is a `lg`-and-up layout. */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-[1.1fr_1fr_0.8fr_1fr_auto]">
        <Field label="Where" className="border-b border-[var(--line)] md:border-r lg:border-b-0">
          <Select value={community} onValueChange={setCommunity}>
            <SelectTrigger className="min-h-[44px] w-full rounded-none border-0 bg-transparent px-0 text-[15px] text-[var(--text)] shadow-none focus-visible:ring-0 lg:min-h-0">
              <SelectValue placeholder="Anywhere in Dubai" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Anywhere in Dubai</SelectItem>
              {communities.map((c) => (
                <SelectItem key={c.slug} value={c.name}>
                  {c.name} <span className="text-[var(--text-muted)]">· {c.count}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field label="Type" className="border-b border-[var(--line)] lg:border-b-0 lg:border-r">
          <Select value={propertyType} onValueChange={setPropertyType}>
            <SelectTrigger className="min-h-[44px] w-full rounded-none border-0 bg-transparent px-0 text-[15px] text-[var(--text)] shadow-none focus-visible:ring-0 lg:min-h-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {propertyTypes.map((t) => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field label="Beds" className="border-b border-[var(--line)] md:border-r lg:border-b-0">
          <Select value={beds_} onValueChange={setBeds}>
            <SelectTrigger className="min-h-[44px] w-full rounded-none border-0 bg-transparent px-0 text-[15px] text-[var(--text)] shadow-none focus-visible:ring-0 lg:min-h-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {beds.map((b) => (
                <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field label="Budget" className="border-b border-[var(--line)] lg:border-b-0 lg:border-r">
          <Select value={price} onValueChange={setPrice}>
            <SelectTrigger className="min-h-[44px] w-full rounded-none border-0 bg-transparent px-0 text-[15px] text-[var(--text)] shadow-none focus-visible:ring-0 lg:min-h-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {priceRanges.map((p) => (
                <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <button
          type="submit"
          className="flex h-full min-h-[56px] w-full items-center justify-center gap-3 bg-[var(--cream)] px-6 text-[11.5px] uppercase tracking-[0.22em] text-[var(--navy)] transition-colors hover:bg-[#e6dab0] sm:min-h-[64px] md:col-span-2 lg:col-span-1 lg:w-auto lg:px-10"
        >
          Find Homes
          <ArrowRight className="size-4" />
        </button>
      </div>

      <div className="flex items-center justify-between border-t border-[var(--line)] px-4 py-1 md:px-6 md:py-2.5">
        <button
          type="button"
          onClick={() => setShowAdvanced((v) => !v)}
          aria-expanded={showAdvanced}
          className="inline-flex min-h-[44px] items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[var(--text-muted)] hover:text-[var(--cream)] md:min-h-0"
        >
          <SlidersHorizontal className="size-3.5" />
          {showAdvanced ? "Hide filters" : "More filters"}
          {advancedActiveCount > 0 && (
            <span className="ml-1 inline-flex h-5 min-w-[20px] items-center justify-center bg-[var(--cream)] px-1.5 text-[10px] font-medium tracking-normal text-[var(--navy)]">
              {advancedActiveCount}
            </span>
          )}
        </button>
        {advancedActiveCount > 0 && (
          <button
            type="button"
            onClick={() => {
              setBaths("any");
              setMinArea("");
              setMaxArea("");
              setNewOnly(false);
            }}
            className="inline-flex min-h-[44px] items-center gap-1.5 px-2 text-[11px] uppercase tracking-[0.2em] text-[var(--text-muted)] hover:text-[var(--cream)] md:min-h-0 md:px-0"
          >
            <X className="size-3" />
            Clear
          </button>
        )}
      </div>

      {showAdvanced && (
        <div className="grid grid-cols-1 gap-x-6 gap-y-4 border-t border-[var(--line)] bg-[var(--surface-2)] px-4 py-5 sm:grid-cols-2 md:grid-cols-3 md:px-6 md:py-6">
          <Field label="Bathrooms">
            <Select value={baths_} onValueChange={setBaths}>
              <SelectTrigger className="h-10 min-h-[44px] w-full rounded-none border-0 border-b border-[var(--line-strong)] bg-transparent px-0 text-[14px] lg:min-h-0 text-[var(--text)] shadow-none focus-visible:ring-0">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {baths.map((b) => (
                  <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field label="Min area (sqft)">
            <input
              type="number"
              inputMode="numeric"
              min="0"
              step="100"
              value={minArea}
              onChange={(e) => setMinArea(e.target.value)}
              placeholder="e.g. 1200"
              className="h-10 min-h-[44px] w-full rounded-none border-0 border-b border-[var(--line-strong)] bg-transparent px-0 text-[14px] lg:min-h-0 text-[var(--text)] outline-none placeholder:text-[var(--text-muted)] focus:border-[var(--cream)]"
            />
          </Field>

          <Field label="Max area (sqft)">
            <input
              type="number"
              inputMode="numeric"
              min="0"
              step="100"
              value={maxArea}
              onChange={(e) => setMaxArea(e.target.value)}
              placeholder="e.g. 3500"
              className="h-10 min-h-[44px] w-full rounded-none border-0 border-b border-[var(--line-strong)] bg-transparent px-0 text-[14px] lg:min-h-0 text-[var(--text)] outline-none placeholder:text-[var(--text-muted)] focus:border-[var(--cream)]"
            />
          </Field>

          <label className="col-span-1 inline-flex min-h-[44px] cursor-pointer items-center gap-3 text-[14px] text-[var(--text)] sm:col-span-2 md:col-span-3 lg:min-h-0">
            <input
              type="checkbox"
              checked={newOnly}
              onChange={(e) => setNewOnly(e.target.checked)}
              className="size-4 cursor-pointer accent-[var(--cream)]"
            />
            <span>Only show <strong className="font-medium text-[var(--cream)]">newly listed</strong> (last 7 days)</span>
          </label>
        </div>
      )}
      </>
      )}
    </form>
  );
}

function Field({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex min-w-0 flex-col px-4 py-2 md:px-6 md:py-2.5", className)}>
      <span className="eyebrow text-[var(--text-muted)]">{label}</span>
      {children}
    </div>
  );
}
